import { useState, useEffect } from 'react';
import { useSettings } from '@/hooks/useSettings';
import { useBusinessGoals, BusinessGoal, getGoalStatus, checkDateOverlap } from '@/hooks/useBusinessGoals';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useTranslation } from '@/contexts/LanguageContext';
import { useProduct } from '@/contexts/ProductContext';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { PlanLimitTooltip } from '@/components/ui/plan-limit-tooltip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { GoalDateRangePicker } from '@/components/GoalDateRangePicker';
import { CurrencyInput } from '@/components/CurrencyInput';
import { format, differenceInMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Bell, 
  Target, 
  AlertTriangle, 
  Settings as SettingsIcon,
  Plus,
  Trash2,
  DollarSign,
  Users,
  TrendingDown,
  Package,
  Calendar,
  Pencil
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { z } from 'zod';
import { cn } from '@/lib/utils';

const goalSchema = z.object({
  goal_type: z.enum(['mrr', 'arr', 'customers', 'churn']),
  target_value: z.number().positive({ message: "Valor deve ser positivo" }),
  period: z.enum(['daily', 'monthly', 'quarterly', 'semiannual', 'yearly']),
  start_date: z.string().min(1, { message: "Data de início obrigatória" }),
  end_date: z.string().min(1, { message: "Data de término obrigatória" }),
}).refine((data) => {
  if (data.goal_type === 'churn' && data.target_value > 100) {
    return false;
  }
  if (data.goal_type === 'customers' && !Number.isInteger(data.target_value)) {
    return false;
  }
  return true;
}, {
  message: "Valor inválido para o tipo de meta selecionado",
  path: ["target_value"]
});

export default function Settings() {
  const { settings, updateSettings, isLoading } = useSettings();
  const { goals, createGoal, updateGoal, deleteGoal, isLoading: loadingGoals } = useBusinessGoals();
  const { isAdmin } = useUserProfile();
  const { t } = useTranslation();
  const { products, selectedProduct } = useProduct();
  const { plan, canAddGoal, getGoalsConfig } = useSubscriptionLimits();
  
  const [canCreateGoal, setCanCreateGoal] = useState(false);
  const [currentGoalsCount, setCurrentGoalsCount] = useState(0);
  const goalsConfig = getGoalsConfig();

  useEffect(() => {
    document.title = 'Dashi | Configurações';
  }, []);

  useEffect(() => {
    const checkGoalsLimit = async () => {
      if (!goalsConfig.enabled) {
        setCanCreateGoal(false);
        return;
      }
      
      const activeGoals = goals?.filter(g => g.is_active) || [];
      setCurrentGoalsCount(activeGoals.length);
      
      const hasSlots = await canAddGoal();
      setCanCreateGoal(hasSlots);
    };
    checkGoalsLimit();
  }, [goals, goalsConfig.enabled, canAddGoal]);

  // Helper function to format audit information
  const formatAuditInfo = (
    dateString: string, 
    userName: string | null | undefined,
    translationKey: 'goalCreatedOn' | 'lastEditedOn'
  ) => {
    const date = new Date(dateString);
    const formattedDate = format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    const byUser = userName ? ` ${t('by')} ${userName}` : '';
    return `${t(translationKey)} ${formattedDate}${byUser}`;
  };
  
  const [openGoalDialog, setOpenGoalDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState<BusinessGoal | null>(null);
  const [filterProductId, setFilterProductId] = useState<string | null>(null);
  const [newGoal, setNewGoal] = useState<{
    goal_type: 'mrr' | 'arr' | 'customers' | 'churn';
    target_value: number;
    period: 'daily' | 'monthly' | 'quarterly' | 'semiannual' | 'yearly';
    start_date: Date | null;
    end_date: Date | null;
    product_id: string | null;
    currency: 'BRL' | 'USD';
  }>({
    goal_type: 'mrr',
    target_value: 0,
    period: 'monthly',
    start_date: null,
    end_date: null,
    product_id: selectedProduct?.id || null,
    currency: 'BRL',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const hasConflict = newGoal.start_date && newGoal.end_date 
    ? checkDateOverlap(
        goals,
        newGoal.start_date.toISOString().split('T')[0],
        newGoal.end_date.toISOString().split('T')[0],
        newGoal.goal_type,
        newGoal.product_id
      )
    : false;

  // Apply product filter
  const filteredGoals = filterProductId 
    ? goals?.filter(goal => goal.product_id === filterProductId)
    : goals;

  const handleToggle = async (field: string, value: boolean) => {
    await updateSettings.mutateAsync({ [field]: value });
  };

  const handleThresholdChange = async (value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      await updateSettings.mutateAsync({ churn_threshold: numValue });
    }
  };

  const handleThemeChange = async (value: string) => {
    await updateSettings.mutateAsync({ theme: value });
  };

  const handleTimezoneChange = async (value: string) => {
    await updateSettings.mutateAsync({ timezone: value });
  };

  const handleLanguageChange = async (value: string) => {
    await updateSettings.mutateAsync({ language: value });
  };

  const getGoalTypeLabel = (type: string) => {
    const labels: Record<string, any> = {
      mrr: t('mrr'),
      arr: t('arr'),
      customers: t('customers'),
      churn: t('churn'),
    };
    return labels[type] || type;
  };

  const getGoalTypeIcon = (type: string) => {
    const icons = {
      mrr: DollarSign,
      arr: DollarSign,
      customers: Users,
      churn: TrendingDown,
    };
    const Icon = icons[type as keyof typeof icons] || Target;
    return <Icon className="h-4 w-4" />;
  };

  const getPeriodLabel = (period: string) => {
    const labels: Record<string, any> = {
      daily: t('daily'),
      monthly: t('monthly'),
      quarterly: t('quarterly'),
      semiannual: t('semiannual'),
      yearly: t('yearly'),
    };
    return labels[period] || period;
  };

  const getStatusBadge = (status: ReturnType<typeof getGoalStatus>) => {
    const badges = {
      active: <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">{t('activeGoal')}</Badge>,
      future: <Badge className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20">{t('futureGoal')}</Badge>,
      expired: <Badge variant="outline" className="opacity-60">{t('expiredGoal')}</Badge>,
    };
    return badges[status];
  };

  const handleCreateGoal = async () => {
    try {
      setErrors({});
      
      if (!newGoal.start_date || !newGoal.end_date) {
        setErrors({ date: 'Datas obrigatórias' });
        return;
      }

      if (hasConflict) {
        setErrors({ date: t('dateConflict') });
        return;
      }

      const validated = goalSchema.parse({
        ...newGoal,
        start_date: newGoal.start_date.toISOString().split('T')[0],
        end_date: newGoal.end_date.toISOString().split('T')[0],
      });

      await createGoal.mutateAsync({
        goal_type: validated.goal_type,
        target_value: validated.target_value,
        period: validated.period,
        start_date: validated.start_date,
        end_date: validated.end_date,
        year: new Date(validated.start_date).getFullYear(),
        month: null,
        quarter: null,
        is_active: true,
        product_id: newGoal.product_id,
        currency: newGoal.currency,
      });
      
      setOpenGoalDialog(false);
      setNewGoal({
        goal_type: 'mrr',
        target_value: 0,
        period: 'monthly',
        start_date: null,
        end_date: null,
        product_id: selectedProduct?.id || null,
        currency: 'BRL',
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  const handleEditGoal = async () => {
    if (!editingGoal) return;
    
    try {
      setErrors({});
      
      if (!newGoal.start_date || !newGoal.end_date) {
        setErrors({ date: 'Datas obrigatórias' });
        return;
      }

      const editHasConflict = checkDateOverlap(
        goals,
        newGoal.start_date.toISOString().split('T')[0],
        newGoal.end_date.toISOString().split('T')[0],
        newGoal.goal_type,
        newGoal.product_id,
        editingGoal.id
      );

      if (editHasConflict) {
        setErrors({ date: t('dateConflict') });
        return;
      }

      const validated = goalSchema.parse({
        ...newGoal,
        start_date: newGoal.start_date.toISOString().split('T')[0],
        end_date: newGoal.end_date.toISOString().split('T')[0],
      });

      await updateGoal.mutateAsync({
        id: editingGoal.id,
        updates: {
          goal_type: validated.goal_type,
          target_value: validated.target_value,
          period: validated.period,
          start_date: validated.start_date,
          end_date: validated.end_date,
          year: new Date(validated.start_date).getFullYear(),
          product_id: newGoal.product_id,
          currency: newGoal.currency,
        },
      });

      setOpenEditDialog(false);
      setEditingGoal(null);
      setNewGoal({
        goal_type: 'mrr',
        target_value: 0,
        period: 'monthly',
        start_date: null,
        end_date: null,
        product_id: selectedProduct?.id || null,
        currency: 'BRL',
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  const openEditGoalDialog = (goal: BusinessGoal) => {
    setEditingGoal(goal);
    setNewGoal({
      goal_type: goal.goal_type,
      target_value: goal.target_value,
      period: goal.period,
      start_date: new Date(goal.start_date),
      end_date: new Date(goal.end_date),
      product_id: goal.product_id,
      currency: goal.currency,
    });
    setErrors({});
    setOpenEditDialog(true);
  };

  if (isLoading || loadingGoals) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          {t('settingsTitle')}
        </h1>
        <p className="text-muted-foreground mt-1">{t('settingsDescription')}</p>
      </div>

      <Tabs defaultValue="goals" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="goals">
            <Target className="h-4 w-4 mr-2" />
            {t('goals')}
          </TabsTrigger>
          <TabsTrigger value="alerts">
            <AlertTriangle className="h-4 w-4 mr-2" />
            {t('alerts')}
          </TabsTrigger>
          <TabsTrigger value="preferences">
            <SettingsIcon className="h-4 w-4 mr-2" />
            {t('preferences')}
          </TabsTrigger>
        </TabsList>

        {/* Metas */}
        <TabsContent value="goals" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('businessGoals')}</CardTitle>
                  <CardDescription>{t('businessGoalsDescription')}</CardDescription>
                </div>
                <Dialog open={openGoalDialog} onOpenChange={setOpenGoalDialog}>
                  <DialogTrigger asChild>
                    <PlanLimitTooltip
                      enabled={canCreateGoal && isAdmin}
                      planName={plan?.name || 'atual'}
                      limitType={goalsConfig.enabled ? 'limit' : 'feature'}
                      featureName="metas"
                      currentCount={currentGoalsCount}
                      maxCount={goalsConfig.maxGoals}
                    >
                      <Button disabled={!canCreateGoal || !isAdmin}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('newGoal')}
                      </Button>
                    </PlanLimitTooltip>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('createNewGoal')}</DialogTitle>
                      <DialogDescription>{t('createNewGoalDescription')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('product')}</Label>
                        <Select
                          value={newGoal.product_id || 'all'}
                          onValueChange={(value) => setNewGoal({ ...newGoal, product_id: value === 'all' ? null : value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                <span>{t('allProducts')}</span>
                              </div>
                            </SelectItem>
                            {products.map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                <div className="flex items-center gap-2">
                                  {product.logo_url && (
                                    <img
                                      src={product.logo_url}
                                      alt={product.name}
                                      className="h-4 w-4 object-contain"
                                    />
                                  )}
                                  <span>{product.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('goalType')}</Label>
                        <Select
                          value={newGoal.goal_type}
                          onValueChange={(value: any) => {
                            const updates: any = { goal_type: value, target_value: 0 };
                            // Lock period to 'yearly' when ARR is selected
                            if (value === 'arr') {
                              updates.period = 'yearly';
                              // Auto-set current year for ARR
                              const currentYear = new Date().getFullYear();
                              updates.start_date = new Date(currentYear, 0, 1);
                              updates.end_date = new Date(currentYear, 11, 31);
                            }
                            // Auto-set current month/year for MRR
                            if (value === 'mrr') {
                              updates.period = 'monthly';
                              const now = new Date();
                              const year = now.getFullYear();
                              const month = now.getMonth();
                              updates.start_date = new Date(year, month, 1);
                              const lastDay = new Date(year, month + 1, 0).getDate();
                              updates.end_date = new Date(year, month, lastDay);
                            }
                            setNewGoal({ ...newGoal, ...updates });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mrr">MRR</SelectItem>
                            <SelectItem value="arr">ARR</SelectItem>
                            <SelectItem value="customers">{t('customers')}</SelectItem>
                            <SelectItem value="churn">Churn (%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newGoal.goal_type === 'churn' ? (
                        <div className="space-y-2">
                          <Label>{t('targetValue')}</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={newGoal.target_value}
                              onChange={(e) => setNewGoal({ ...newGoal, target_value: parseFloat(e.target.value) || 0 })}
                              placeholder="0.00"
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                        </div>
                      ) : newGoal.goal_type === 'customers' ? (
                        <div className="space-y-2">
                          <Label>{t('targetValue')}</Label>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={newGoal.target_value}
                            onChange={(e) => setNewGoal({ ...newGoal, target_value: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                          />
                        </div>
                      ) : (
                        <CurrencyInput
                          value={newGoal.target_value}
                          currency={newGoal.currency}
                          onValueChange={(value) => setNewGoal({ ...newGoal, target_value: value })}
                          onCurrencyChange={(currency) => setNewGoal({ ...newGoal, currency })}
                        />
                      )}
                      {errors.target_value && <p className="text-sm text-destructive">{errors.target_value}</p>}
                      <div className="space-y-2">
                        <Label>{t('period')}</Label>
                        <Select
                          value={newGoal.period}
                          onValueChange={(value: any) => setNewGoal({ ...newGoal, period: value })}
                          disabled={newGoal.goal_type === 'arr' || newGoal.goal_type === 'mrr'}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">{t('daily')}</SelectItem>
                            <SelectItem value="monthly">{t('monthly')}</SelectItem>
                            <SelectItem value="quarterly">{t('quarterly')}</SelectItem>
                            <SelectItem value="semiannual">{t('semiannual')}</SelectItem>
                            <SelectItem value="yearly">{t('yearly')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <GoalDateRangePicker
                          startDate={newGoal.start_date}
                          endDate={newGoal.end_date}
                        onStartDateChange={(date) => setNewGoal(prev => ({ ...prev, start_date: date }))}
                        onEndDateChange={(date) => setNewGoal(prev => ({ ...prev, end_date: date }))}
                          period={newGoal.period}
                          hasConflict={hasConflict}
                          goalType={newGoal.goal_type}
                        />
                        {errors.date && <p className="text-sm text-destructive">{errors.date}</p>}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOpenGoalDialog(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleCreateGoal} disabled={createGoal.isPending}>
                        {createGoal.isPending ? t('creating') : t('createGoal')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Filter Controls */}
                <div className="flex items-center gap-3 pb-2 border-b">
                  <Label className="text-sm font-medium">{t('filterByProduct')}:</Label>
                  <Select
                    value={filterProductId || 'all'}
                    onValueChange={(value) => setFilterProductId(value === 'all' ? null : value)}
                  >
                    <SelectTrigger className="w-[240px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          <span>{t('allProducts')}</span>
                        </div>
                      </SelectItem>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          <div className="flex items-center gap-2">
                            {product.logo_url && (
                              <img
                                src={product.logo_url}
                                alt={product.name}
                                className="h-4 w-4 object-contain"
                              />
                            )}
                            <span>{product.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filterProductId && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFilterProductId(null)}
                    >
                      {t('clearFilter')}
                    </Button>
                  )}
                  <div className="ml-auto text-sm text-muted-foreground">
                    {filterProductId ? (
                      <span>
                        {t('showingXGoalsFor')
                          .replace('{x}', String(filteredGoals?.length || 0))
                          .replace('{product}', products.find(p => p.id === filterProductId)?.name || '')}
                      </span>
                    ) : (
                      <span>
                        {t('showingXGoals').replace('{x}', String(filteredGoals?.length || 0))}
                      </span>
                    )}
                  </div>
                </div>

                {/* Goals List */}
                {filteredGoals && filteredGoals.length > 0 ? (
                  <div className="space-y-3">
                    {[...filteredGoals]
                    .sort((a, b) => {
                      const statusOrder = { active: 0, future: 1, expired: 2 };
                      return statusOrder[getGoalStatus(a)] - statusOrder[getGoalStatus(b)];
                    })
                    .map((goal) => {
                      const status = getGoalStatus(goal);
                      const startDate = new Date(goal.start_date);
                      const endDate = new Date(goal.end_date);
                      const durationMonths = differenceInMonths(endDate, startDate);
                      
                      return (
                        <div 
                          key={goal.id} 
                          className={cn(
                            "flex items-center justify-between p-4 border rounded-lg",
                            status === 'active' && "border-primary/50 bg-primary/5"
                          )}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              {getGoalTypeIcon(goal.goal_type)}
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{getGoalTypeLabel(goal.goal_type)}</p>
                                {getStatusBadge(status)}
                                {goal.product ? (
                                  <Badge variant="secondary" className="flex items-center gap-1">
                                    {goal.product.logo_url && (
                                      <img
                                        src={goal.product.logo_url}
                                        alt={goal.product.name}
                                        className="h-3 w-3 object-contain"
                                      />
                                    )}
                                    <span>{goal.product.name}</span>
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="flex items-center gap-1">
                                    <Package className="h-3 w-3" />
                                    <span>{t('globalGoal')}</span>
                                  </Badge>
                                )}
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm text-muted-foreground">
                                  {t('target')}: {
                                    goal.goal_type === 'churn' 
                                      ? `${goal.target_value.toFixed(2)}%`
                                      : goal.goal_type === 'customers'
                                        ? goal.target_value.toFixed(0)
                                        : goal.target_value.toLocaleString(goal.currency === 'BRL' ? 'pt-BR' : 'en-US', { 
                                            style: 'currency', 
                                            currency: goal.currency 
                                          })
                                  } • {getPeriodLabel(goal.period)}
                                </p>
                                 <p className="text-sm text-muted-foreground flex items-center gap-1">
                                   <Calendar className="h-3 w-3" />
                                   {t('validityPeriod')}: {format(startDate, 'dd/MM/yyyy')} - {format(endDate, 'dd/MM/yyyy')}
                                   <span className="text-xs">({durationMonths} {t('months')})</span>
                                 </p>
                    <div className="space-y-1 mt-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground">
                        {formatAuditInfo(
                          goal.created_at, 
                          goal.creator?.full_name, 
                          'goalCreatedOn'
                        )}
                      </p>
                      {goal.updated_at !== goal.created_at && goal.updated_by && (
                        <p className="text-xs text-muted-foreground">
                          {formatAuditInfo(
                            goal.updated_at, 
                            goal.updater?.full_name, 
                            'lastEditedOn'
                          )}
                        </p>
                      )}
                    </div>
                               </div>
                             </div>
                           </div>
                           <div className="flex gap-2">
                             <Button
                               variant="ghost"
                               size="icon"
                               onClick={() => openEditGoalDialog(goal)}
                             >
                               <Pencil className="h-4 w-4" />
                             </Button>
                             <Button
                               variant="ghost"
                               size="icon"
                               onClick={() => deleteGoal.mutate(goal.id)}
                             >
                               <Trash2 className="h-4 w-4 text-destructive" />
                             </Button>
                           </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="font-medium">
                      {filterProductId ? t('noGoalsForProduct') : t('noGoalsDefined')}
                    </p>
                    <p className="text-sm">
                      {filterProductId ? t('tryAnotherProduct') : t('clickNewGoal')}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Goal Dialog */}
          <Dialog open={openEditDialog} onOpenChange={setOpenEditDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('editGoal')}</DialogTitle>
                <DialogDescription>{t('editGoalDescription')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{t('product')}</Label>
                  <Select
                    value={newGoal.product_id || 'all'}
                    onValueChange={(value) => setNewGoal({ ...newGoal, product_id: value === 'all' ? null : value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          <span>{t('allProducts')}</span>
                        </div>
                      </SelectItem>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          <div className="flex items-center gap-2">
                            {product.logo_url && (
                              <img
                                src={product.logo_url}
                                alt={product.name}
                                className="h-4 w-4 object-contain"
                              />
                            )}
                            <span>{product.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('goalType')}</Label>
                  <Select
                    value={newGoal.goal_type}
                    onValueChange={(value: any) => {
                      const updates: any = { goal_type: value, target_value: 0 };
                      // Lock period to 'yearly' when ARR is selected
                      if (value === 'arr') {
                        updates.period = 'yearly';
                        // Auto-set current year for ARR
                        const currentYear = new Date().getFullYear();
                        updates.start_date = new Date(currentYear, 0, 1);
                        updates.end_date = new Date(currentYear, 11, 31);
                      }
                      // Auto-set current month/year for MRR
                      if (value === 'mrr') {
                        updates.period = 'monthly';
                        const now = new Date();
                        const year = now.getFullYear();
                        const month = now.getMonth();
                        updates.start_date = new Date(year, month, 1);
                        const lastDay = new Date(year, month + 1, 0).getDate();
                        updates.end_date = new Date(year, month, lastDay);
                      }
                      setNewGoal({ ...newGoal, ...updates });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mrr">MRR</SelectItem>
                      <SelectItem value="arr">ARR</SelectItem>
                      <SelectItem value="customers">{t('customers')}</SelectItem>
                      <SelectItem value="churn">Churn (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newGoal.goal_type === 'churn' ? (
                  <div className="space-y-2">
                    <Label>{t('targetValue')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={newGoal.target_value}
                        onChange={(e) => setNewGoal({ ...newGoal, target_value: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                ) : newGoal.goal_type === 'customers' ? (
                  <div className="space-y-2">
                    <Label>{t('targetValue')}</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={newGoal.target_value}
                      onChange={(e) => setNewGoal({ ...newGoal, target_value: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>
                ) : (
                  <CurrencyInput
                    value={newGoal.target_value}
                    currency={newGoal.currency}
                    onValueChange={(value) => setNewGoal({ ...newGoal, target_value: value })}
                    onCurrencyChange={(currency) => setNewGoal({ ...newGoal, currency })}
                  />
                )}
                {errors.target_value && <p className="text-sm text-destructive">{errors.target_value}</p>}
                <div className="space-y-2">
                  <Label>{t('period')}</Label>
                  <Select
                    value={newGoal.period}
                    onValueChange={(value: any) => setNewGoal({ ...newGoal, period: value })}
                    disabled={newGoal.goal_type === 'arr' || newGoal.goal_type === 'mrr'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">{t('daily')}</SelectItem>
                      <SelectItem value="monthly">{t('monthly')}</SelectItem>
                      <SelectItem value="quarterly">{t('quarterly')}</SelectItem>
                      <SelectItem value="semiannual">{t('semiannual')}</SelectItem>
                      <SelectItem value="yearly">{t('yearly')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <GoalDateRangePicker
                    startDate={newGoal.start_date}
                    endDate={newGoal.end_date}
                    onStartDateChange={(date) => setNewGoal(prev => ({ ...prev, start_date: date }))}
                    onEndDateChange={(date) => setNewGoal(prev => ({ ...prev, end_date: date }))}
                    period={newGoal.period}
                    goalType={newGoal.goal_type}
                    hasConflict={editingGoal ? checkDateOverlap(
                      goals,
                      newGoal.start_date?.toISOString().split('T')[0] || '',
                      newGoal.end_date?.toISOString().split('T')[0] || '',
                      newGoal.goal_type,
                      newGoal.product_id,
                      editingGoal.id
                    ) : false}
                  />
                  {errors.date && <p className="text-sm text-destructive">{errors.date}</p>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setOpenEditDialog(false);
                  setEditingGoal(null);
                  setErrors({});
                }}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleEditGoal} disabled={updateGoal.isPending}>
                  {updateGoal.isPending ? t('updating') : t('updateGoal')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Alertas */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('automaticAlerts')}</CardTitle>
              <CardDescription>{t('automaticAlertsDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('highChurnAlert')}</Label>
                  <p className="text-sm text-muted-foreground">{t('highChurnAlertDescription')}</p>
                </div>
                <Switch
                  checked={settings?.alert_high_churn}
                  onCheckedChange={(checked) => handleToggle('alert_high_churn', checked)}
                />
              </div>
              {settings?.alert_high_churn && (
                <div className="ml-4 space-y-2">
                  <Label htmlFor="churn_threshold">{t('churnThreshold')}</Label>
                  <Input
                    id="churn_threshold"
                    type="number"
                    step="0.01"
                    value={settings.churn_threshold}
                    onChange={(e) => handleThresholdChange(e.target.value)}
                    className="max-w-[200px]"
                  />
                </div>
              )}
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('lowRevenueAlert')}</Label>
                  <p className="text-sm text-muted-foreground">{t('lowRevenueAlertDescription')}</p>
                </div>
                <Switch
                  checked={settings?.alert_low_revenue}
                  onCheckedChange={(checked) => handleToggle('alert_low_revenue', checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('goalAchievedAlert')}</Label>
                  <p className="text-sm text-muted-foreground">{t('goalAchievedAlertDescription')}</p>
                </div>
                <Switch
                  checked={settings?.alert_goal_achieved}
                  onCheckedChange={(checked) => handleToggle('alert_goal_achieved', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferências */}
        <TabsContent value="preferences" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('generalPreferences')}</CardTitle>
              <CardDescription>{t('generalPreferencesDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('theme')}</Label>
                <Select
                  value={settings?.theme || 'light'}
                  onValueChange={handleThemeChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{t('themeLight')}</SelectItem>
                    <SelectItem value="dark">{t('themeDark')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('timezone')}</Label>
                <Select
                  value={settings?.timezone || 'America/Sao_Paulo'}
                  onValueChange={handleTimezoneChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Sao_Paulo">Brasília (GMT-3)</SelectItem>
                    <SelectItem value="America/New_York">Nova York (GMT-5)</SelectItem>
                    <SelectItem value="Europe/London">Londres (GMT+0)</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tóquio (GMT+9)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('language')}</Label>
                <Select
                  value={settings?.language || 'pt-BR'}
                  onValueChange={handleLanguageChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    <SelectItem value="en-US">English (US)</SelectItem>
                    <SelectItem value="es-ES">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
